import { useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Save, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const MAX_STARTERS = 11;
const POS_RANK = { GK: 0, DEF: 1, MID: 2, FWD: 3 };
const toEntry = (p) => ({ player_id: p.player_id, player_name: p.player_name, number: p.number ?? null, position: p.position || null });

// Defined at module level so parent re-renders don't remount it (keeps list scroll position).
export const LineupEditor = ({ side, state, setState, teamName, players, matchId, teamId, onChange, prefilledFrom, onSaved }) => {
  const squadByPid = useMemo(() => Object.fromEntries(players.map((p) => [p.player_id, p])), [players]);

  const knownAll = useMemo(() => {
    const seen = new Set();
    const merged = [];
    const add = (e) => { if (e.player_id && !seen.has(e.player_id)) { seen.add(e.player_id); merged.push(e); } };
    players.forEach((p) => add({ player_id: p.player_id, player_name: p.name, number: p.number ?? null, position: p.position || null }));
    [...(state.starters || []), ...(state.substitutes || [])].forEach((p) => add(toEntry(p)));
    merged.sort((a, b) => ((POS_RANK[a.position] ?? 9) - (POS_RANK[b.position] ?? 9)) || ((a.number ?? 99) - (b.number ?? 99)));
    return merged;
  }, [players, state.starters, state.substitutes]);

  const starterIds = useMemo(() => new Set((state.starters || []).map((p) => p.player_id).filter(Boolean)), [state.starters]);
  const captainId = (state.starters || []).find((p) => p.is_captain)?.player_id || null;

  const toggleStarter = (p) => {
    if (!starterIds.has(p.player_id) && starterIds.size >= MAX_STARTERS) {
      toast.error(`Max ${MAX_STARTERS} starters — uncheck one first`);
      return;
    }
    setState((s) => {
      if ((s.starters || []).some((x) => x.player_id === p.player_id)) {
        return { ...s, starters: s.starters.filter((x) => x.player_id !== p.player_id) };
      }
      const sq = squadByPid[p.player_id];
      const entry = { player_id: p.player_id, player_name: sq?.name || p.player_name, number: sq?.number ?? p.number ?? null, position: sq?.position || p.position || null, is_captain: false };
      return { ...s, starters: [...(s.starters || []), entry] };
    });
  };

  const toggleCaptain = (pid) => setState((s) => ({
    ...s,
    starters: (s.starters || []).map((p) => ({ ...p, is_captain: p.player_id === pid ? !p.is_captain : false })),
  }));

  // On save: unchecked squad members auto-become substitutes
  const handleSave = async () => {
    const subs = knownAll.filter((p) => !starterIds.has(p.player_id)).map((p) => ({ ...toEntry(p), is_captain: false }));
    setState((s) => ({ ...s, substitutes: subs }));
    try {
      await axios.put(`${API}/matches/${matchId}/lineups`, {
        team_id: teamId,
        formation: state.formation || null,
        coach_name: state.coach_name || null,
        starters: state.starters || [],
        substitutes: subs,
      }, { withCredentials: true });
      toast.success(`${side === "home" ? "Home" : "Away"} lineup saved — ${state.starters.length} starters, ${subs.length} subs`);
      onSaved?.();
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save lineup");
    }
  };

  return (
    <div className="bg-white/5 p-3 space-y-2" data-testid={`lineup-editor-${side}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs uppercase tracking-widest text-gray-400">{teamName}</span>
        <div className="flex items-center gap-2">
          <span className={`text-[11px] font-bold ${starterIds.size === MAX_STARTERS ? "text-[#FFC72C]" : "text-gray-400"}`} data-testid={`lineup-counter-${side}`}>
            XI {starterIds.size}/{MAX_STARTERS}
          </span>
          <Button size="sm" variant="ghost" className="text-[#FFC72C] hover:text-[#FFC72C] h-7 px-2" onClick={handleSave} data-testid={`btn-save-lineup-${side}`}>
            <Save className="w-3.5 h-3.5 mr-1" /> Save
          </Button>
        </div>
      </div>

      {prefilledFrom && (
        <div className="flex items-start gap-2 bg-[#FFC72C]/10 border border-[#FFC72C]/30 px-2 py-1.5 text-[11px] text-[#FFC72C]" data-testid={`lineup-prefilled-${side}`}>
          <History className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Pre-filled from last match{prefilledFrom.opponent ? ` vs ${prefilledFrom.opponent}` : ""}{prefilledFrom.match_date ? ` (${prefilledFrom.match_date})` : ""}. Untick anyone who isn't starting, then press Save.</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Formation (4-3-3)" value={state.formation} onChange={(e) => setState((s) => ({ ...s, formation: e.target.value }))} className="bg-white/10 border-white/10 text-white text-xs h-8" data-testid={`input-formation-${side}`} />
        <Input placeholder="Coach name" value={state.coach_name} onChange={(e) => setState((s) => ({ ...s, coach_name: e.target.value }))} className="bg-white/10 border-white/10 text-white text-xs h-8" data-testid={`input-coach-${side}`} />
      </div>

      <p className="text-[10px] uppercase text-gray-500 mt-2">Tick to add to Starting XI · unchecked squad members are saved as substitutes</p>

      {knownAll.length === 0 ? (
        <p className="text-xs text-gray-400 italic py-4 text-center">No players registered for this team yet.</p>
      ) : (
        <ul className="max-h-72 overflow-y-auto space-y-0.5 pr-1" data-testid={`lineup-list-${side}`}>
          {knownAll.map((p) => {
            const checked = starterIds.has(p.player_id);
            const isCaptain = captainId === p.player_id;
            return (
              <li key={p.player_id} onClick={() => toggleStarter(p)} data-testid={`lineup-row-${side}-${p.player_id}`}
                className={`flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer transition-colors ${checked ? "bg-[#FFC72C]/15 border-l-2 border-[#FFC72C]" : "bg-white/5 border-l-2 border-transparent hover:bg-white/10"}`}>
                <input type="checkbox" checked={checked} readOnly className="w-3.5 h-3.5 accent-[#FFC72C] pointer-events-none" data-testid={`lineup-checkbox-${side}-${p.player_id}`} />
                <span className="font-mono w-6 text-gray-400 text-right">{p.number ?? "—"}</span>
                <span className="flex-1 text-white truncate">{p.player_name}</span>
                {p.position && <span className="text-gray-400 w-8 text-center">{p.position}</span>}
                {checked && (
                  <button onClick={(e) => { e.stopPropagation(); toggleCaptain(p.player_id); }} title="Toggle captain" data-testid={`lineup-captain-${side}-${p.player_id}`}
                    className={`px-1.5 text-[10px] font-bold rounded ${isCaptain ? "bg-[#FFC72C] text-black" : "bg-white/10 text-gray-300 hover:bg-white/20"}`}>C</button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex justify-between text-[10px] text-gray-500 mt-1">
        <span>Starters: <span className="text-white font-bold">{starterIds.size}</span></span>
        <span>Auto-subs: <span className="text-white font-bold">{knownAll.length - starterIds.size}</span></span>
      </div>
    </div>
  );
};
